import { useMemo, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormTitleBox, LoadingSkeleton } from '../../common/components';
import PaymentOrdersHeader from './components/PaymentOrdersHeader';
import PaymentOrdersTable from './components/PaymentOrdersTable';
import PaymentOrderDialog from './components/PaymentOrderDialog';
import DeletePaymentOrderDialog from './components/DeletePaymentOrderDialog';

import {
  usePaymentOrders,
  useDeletePaymentOrder,
  usePrintPaymentOrder,
} from '../../hooks/usePaymentOrders';
import { useLegalEntities } from '../../hooks/useLegalEntities';
import usePaymentOrdersController from './hooks/usePaymentOrdersController';
import { PAYMENT_ORDER_STATUS_FILTERS } from './constants/paymentOrdersConstants';

const PaymentOrders = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

  const { data: legalEntities = [] } = useLegalEntities();

  const legalEntityMap = useMemo(() => {
    const map = {};
    legalEntities.forEach((entity) => {
      map[entity.uuid] = entity.legal_business_name || entity.company || entity.cuit || entity.uuid;
    });
    return map;
  }, [legalEntities]);

  const {
    filters,
    apiFilters,
    searchTerm,
    handleFilterChange,
    handleSearchChange,
    setPage,
    rowsPerPage,
    handleRowsPerPageChange,
    computeOrdersView,
  } = usePaymentOrdersController(legalEntityMap);

  const {
    data: paymentOrders = [],
    isLoading,
    isFetching,
  } = usePaymentOrders(apiFilters);

  const { paginatedOrders, totalPages, currentPage, totalCount } = useMemo(
    () => computeOrdersView(paymentOrders),
    [computeOrdersView, paymentOrders],
  );

  const deleteMutation = useDeletePaymentOrder();
  const printMutation = usePrintPaymentOrder();

  const handleOpenDialog = (orderId = null) => {
    setEditingOrderId(orderId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOrderId(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.uuid);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting payment order:', error);
    }
  };

  const handlePrint = async (orderId) => {
    try {
      await printMutation.mutateAsync(orderId);
    } catch (error) {
      console.error('Error printing payment order:', error);
    }
  };

  const handleShowFeedback = useCallback((message, severity = 'success') => {
    setFeedback({ open: true, message, severity });
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedback((prev) => ({ ...prev, open: false }));
  }, []);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <FormTitleBox
        title="Bancos | Órdenes de pago"
        showButton
        buttonText="Agregar orden de pago"
        buttonIcon={AddIcon}
        onButtonClick={() => handleOpenDialog(null)}
      />

      <PaymentOrdersHeader
        filters={filters}
        searchTerm={searchTerm}
        legalEntities={legalEntities}
        statusOptions={PAYMENT_ORDER_STATUS_FILTERS}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
      />

      <PaymentOrdersTable
        orders={paginatedOrders}
        isLoading={isLoading}
        isFetching={isFetching}
        page={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        legalEntityMap={legalEntityMap}
        onEdit={handleOpenDialog}
        onDelete={setDeleteTarget}
        onPrint={handlePrint}
        disableActions={deleteMutation.isPending || printMutation.isPending}
      />

      <PaymentOrderDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        paymentOrderId={editingOrderId}
        onSuccess={handleShowFeedback}
        onError={handleShowFeedback}
      />

      <DeletePaymentOrderDialog
        open={Boolean(deleteTarget)}
        order={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFeedback} severity={feedback.severity} sx={{ width: '100%' }}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentOrders;
